import { useAuth0 } from "@auth0/auth0-react";
import { Navigate, useLocation } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { CircularProgress, Box } from '@mui/material';
import { ROLE_IDS } from "../../constants/roles";
import { EmailVerificationRequired } from '../Verification/EmailVerificationRequired';
import { DomainVerificationRequired } from '../Verification/DomainVerificationRequired';
import { API_URLS } from '../../config';

interface RecruiterRouteProps {
  children: React.ReactNode;
}

interface VerificationStatus {
  email_verified: boolean;
  domain_verified: boolean;
  domain_verification_pending: boolean;
  company_name?: string;
  company_domain?: string;
}

/**
 * RecruiterRoute component provides role-based route protection for recruiter-only pages.
 *
 * SECURITY: This component ensures only users with the Manager role can access recruiter routes.
 * If a non-recruiter tries to access these routes, they are redirected to the dashboard.
 */
const RecruiterRoute: React.FC<RecruiterRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const [isCheckingRole, setIsCheckingRole] = useState(true);
  const [isRecruiter, setIsRecruiter] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus | null>(null);

  useEffect(() => {
    const checkRecruiterRole = async () => {
      if (!isAuthenticated || !user) {
        setIsCheckingRole(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const userId = user.sub;

        // Fetch user roles from backend
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || 'https://backend.veltus.io'}/auth/user-roles/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          console.error('Failed to fetch user roles');
          setIsRecruiter(false);
          setIsCheckingRole(false);
          return;
        }

        const data = await response.json();
        const roles = data.roles || [];

        // Check if user has Manager (Recruiter) role
        const hasRecruiterRole = roles.some((role: any) => role.id === ROLE_IDS.MANAGER);
        setIsRecruiter(hasRecruiterRole);

        // If recruiter, check verification status
        if (hasRecruiterRole) {
          const verificationResponse = await fetch(`${API_URLS.BASE}/auth/verification-status/${userId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (verificationResponse.ok) {
            const status = await verificationResponse.json();
            setVerificationStatus(status);
          } else {
            // On error, assume not verified to be safe
            setVerificationStatus({
              email_verified: false,
              domain_verified: false,
              domain_verification_pending: false,
            });
          }
        }
      } catch (error) {
        console.error('Error checking recruiter role:', error);
        setIsRecruiter(false);
      } finally {
        setIsCheckingRole(false);
      }
    };

    checkRecruiterRole();
  }, [isAuthenticated, user, getAccessTokenSilently]);

  // Show loading spinner while checking authentication or role
  if (isLoading || isCheckingRole) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Redirect to dashboard if authenticated but not a recruiter
  if (!isRecruiter) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check email verification for recruiters
  if (verificationStatus && !verificationStatus.email_verified) {
    return <EmailVerificationRequired />;
  }

  // Check domain verification for recruiters
  // BUT allow access to profile setup pages even without domain verification
  const isProfileSetupPath = location.pathname.includes('/recruiter/profile');

  if (verificationStatus && !verificationStatus.domain_verified && !isProfileSetupPath) {
    // Check if profile was just created (prevents redirect loop while Auth0 updates)
    const profileJustCreated = sessionStorage.getItem('profile_just_created') === 'true';
    const storedCompanyName = sessionStorage.getItem('profile_company_name');
    const storedCompanyDomain = sessionStorage.getItem('profile_company_domain');

    // Only show domain verification if they have a company_domain set
    // (meaning they've created their profile)
    if (verificationStatus.company_domain || profileJustCreated) {
      // Use stored data if profile was just created and Auth0 hasn't propagated yet
      const companyName = profileJustCreated && storedCompanyName
        ? storedCompanyName
        : (verificationStatus.company_name || 'your company');
      const companyDomain = profileJustCreated && storedCompanyDomain
        ? storedCompanyDomain
        : (verificationStatus.company_domain || '');

      // Clear the flags once we've shown the verification screen
      if (profileJustCreated) {
        sessionStorage.removeItem('profile_just_created');
        sessionStorage.removeItem('profile_company_name');
        sessionStorage.removeItem('profile_company_domain');
      }

      // Always show verification screen - user must click email link
      // Even if their Auth0 email matches the company domain, they still
      // need to verify by clicking a link sent to their company email
      return (
        <DomainVerificationRequired
          companyName={companyName}
          companyDomain={companyDomain}
          userEmail={user?.email || ''}
        />
      );
    }
    // If no company_domain yet, redirect to profile setup
    if (!isProfileSetupPath) {
      return <Navigate to="/recruiter/profile/setup" replace />;
    }
  }

  // User is authenticated, has recruiter role, and is verified
  return <>{children}</>;
};

export default RecruiterRoute;
